import { STEP_UP_EVENT_TYPE, STEP_UP_MINT_FAILURE_REASON } from './constants';
import type { StepUpMintFailureReason } from './constants';
import { createStepUpOAuthTokenProvider } from './oauthTokenProvider';
import { isStepUpPopupBlockedError, StepUpCancelledError } from './stepUpError';
import type {
  StepUpOAuthConfig,
  StepUpRequiredDetails,
  StepUpTokenProvider,
} from './types';

// Step-up mint lifecycle events, for observability (fedops interactions, BI, UX like
// a popup-blocked snackbar). Built from the shared const enums in ./constants
// (STEP_UP_EVENT_TYPE / STEP_UP_MINT_FAILURE_REASON) so emitter and observers speak
// the same vocabulary.
export type StepUpEvent =
  | { type: typeof STEP_UP_EVENT_TYPE.MINT_STARTED; acr: string }
  | { type: typeof STEP_UP_EVENT_TYPE.MINT_SUCCEEDED; acr: string }
  | {
      type: typeof STEP_UP_EVENT_TYPE.MINT_FAILED;
      acr: string;
      reason: StepUpMintFailureReason;
    };

const mintFailureReason = (error: unknown): StepUpMintFailureReason => {
  if (isStepUpPopupBlockedError(error)) {
    return STEP_UP_MINT_FAILURE_REASON.POPUP_BLOCKED;
  }
  if (error instanceof StepUpCancelledError) {
    return STEP_UP_MINT_FAILURE_REASON.CANCELLED;
  }
  return STEP_UP_MINT_FAILURE_REASON.FAILED;
};

export interface StepUpManagerOptions {
  // Resolves a fresh step-up token for a requirement. Defaults to the OAuth
  // `web_message` + PKCE flow built from `oauthConfig`. Inject to avoid a real
  // window / network in tests, or to plug in a different mechanism.
  tokenProvider?: StepUpTokenProvider;
  // OAuth endpoints/params for the default token provider (ignored when a custom
  // `tokenProvider` is supplied). `clientId` is required for the default flow.
  oauthConfig?: Partial<StepUpOAuthConfig>;
  // Observability hook, called at mint lifecycle points (see StepUpEvent). Purely
  // observational: exceptions it throws are swallowed and never affect the flow.
  onEvent?: (event: StepUpEvent) => void;
}

// Canonical key for an acr requirement. Sorted so an order-insensitive OR-list
// (acr_values) maps to the same token regardless of order; '' = a max_age-only
// step-up. Reuse is exact-match; a stronger token satisfying a weaker requirement
// (acr hierarchy) is intentionally not inferred client-side — a wrong guess would
// just 403 and fall through to a fresh step-up anyway.
export const acrKey = (acrValues: string[]): string =>
  [...acrValues].sort().join(' ');

// Owns the step-up (RFC 9470) token lifecycle shared by BOTH entry points — the
// reactive path (a server STEP_UP_REQUIRED, via the interceptor handler or the
// StepUpHttpClient wrapper) and the client-driven trigger (a UX intent, e.g. an
// "enroll 2FA" click). It is the single source of truth for step-up tokens; the
// handler/wrapper are thin and delegate here.
//
// Tokens are cached by the ACR they satisfy:
//   - `tokenByAcr`    : acr requirement -> the token minted for it.
//   - `acrByEndpoint` : endpoint -> the acr it was last seen to require.
// So a challenge for an acr we already hold a token for is satisfied from cache — no
// new popup — whichever entry point asks; and a repeat call to a known endpoint can
// attach its acr's token up front. Minting is single-flight per acr, so concurrent
// challenges for one acr share a single popup.
//
// NOTE: there is deliberately NO feature-gate here — enabling step-up is the
// consumer's call-site decision (register the handler / construct the wrapper, or
// don't). See the README.
export class StepUpManager {
  private readonly tokenProvider: StepUpTokenProvider;
  private readonly onEvent?: (event: StepUpEvent) => void;

  private readonly tokenByAcr = new Map<string, string>();
  private readonly acrByEndpoint = new Map<string, string>();
  private readonly inFlightByAcr = new Map<string, Promise<string>>();

  constructor(options: StepUpManagerOptions = {}) {
    this.tokenProvider =
      options.tokenProvider ??
      createStepUpOAuthTokenProvider(options.oauthConfig);
    this.onEvent = options.onEvent;
  }

  // Observability only — a throwing observer must never break the step-up flow.
  private emit(event: StepUpEvent): void {
    try {
      this.onEvent?.(event);
    } catch {
      // ignore
    }
  }

  // The token cached for an acr, or null. Explicit null (not undefined) so callers
  // can use it directly as the "attach this token" value.
  cachedTokenForAcr(acr: string): string | null {
    return this.tokenByAcr.get(acr) ?? null;
  }

  // The acr an endpoint was last seen to require (for proactive attach), or undefined.
  knownAcrForEndpoint(endpointKey: string): string | undefined {
    return this.acrByEndpoint.get(endpointKey);
  }

  rememberEndpointAcr(endpointKey: string, acr: string): void {
    this.acrByEndpoint.set(endpointKey, acr);
  }

  // Drop a specific token from the acr cache — called when the server rejects a token
  // we reused, so it is never handed out again (here, to a later request, or to the
  // client-driven trigger). Match-guarded: if a concurrent fresh mint already
  // replaced it, we leave the newer token in place.
  evictToken(acr: string, token: string): void {
    if (this.tokenByAcr.get(acr) === token) {
      this.tokenByAcr.delete(acr);
    }
  }

  // Mint a fresh token for an acr and cache it, single-flight: concurrent challenges
  // for the same acr share one popup, and the result is stored under the acr so later
  // requests (either entry point) reuse it.
  stepUp(acr: string, details: StepUpRequiredDetails): Promise<string> {
    const inFlight = this.inFlightByAcr.get(acr);
    if (inFlight) {
      return inFlight;
    }
    this.emit({ type: STEP_UP_EVENT_TYPE.MINT_STARTED, acr });
    const pending = this.tokenProvider(details)
      .then((token) => {
        this.tokenByAcr.set(acr, token);
        this.emit({ type: STEP_UP_EVENT_TYPE.MINT_SUCCEEDED, acr });
        return token;
      })
      .catch((error) => {
        this.emit({
          type: STEP_UP_EVENT_TYPE.MINT_FAILED,
          acr,
          reason: mintFailureReason(error),
        });
        throw error;
      })
      .finally(() => {
        this.inFlightByAcr.delete(acr);
      });
    this.inFlightByAcr.set(acr, pending);
    return pending;
  }

  // Client-driven step-up: mint (or reuse) a token for `acr` up front, driven by a UX
  // intent instead of a server 403. Reuses a cached token for the acr (no popup) or
  // mints one (single-flight). Because it shares the caches above, a token minted
  // here is reused by the next reactive request for the same acr and vice versa.
  // Resolves the token, or rejects with StepUpCancelledError / StepUpFailedError.
  async promptStepUp(acr: string, maxAge?: number): Promise<string> {
    const key = acrKey([acr]);
    return (
      this.cachedTokenForAcr(key) ??
      this.stepUp(key, { acrValues: [acr], maxAge })
    );
  }
}
